import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const PayPalSettings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState({
    clientId: '',
    clientSecret: '',
    environment: 'live'
  });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/paypal/status');
      setStatus(response.data);
      if (response.data.connected) {
        setCredentials({
          clientId: response.data.client_id || '',
          clientSecret: '••••••••••••••••',
          environment: response.data.environment || 'sandbox'
        });
      }
    } catch (error) {
      console.error('Failed to fetch PayPal status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async () => {
    try {
      if (!credentials.clientId || !credentials.clientSecret || credentials.clientSecret === '••••••••••••••••') {
        toast.error('Please enter valid Client ID and Secret');
        return;
      }

      setSaving(true);
      await api.post('/paypal/configure', credentials);
      toast.success('PayPal configured successfully');
      setShowCredentials(false);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to configure PayPal');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect PayPal?')) {
      return;
    }

    try {
      await api.post('/paypal/disconnect');
      toast.success('PayPal disconnected successfully');
      fetchStatus();
    } catch (error) {
      toast.error('Failed to disconnect PayPal');
    }
  };

  const handleTest = async () => {
    try {
      const response = await api.get('/paypal/test');
      if (response.data.success) {
        toast.success('PayPal connection is working!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('PayPal connection test failed');
    }
  };

  if (loading) {
    return <div>Loading PayPal settings...</div>;
  }

  return (
    <div className="card">
      <h2 style={{ marginBottom: '1rem' }}>PayPal Configuration</h2>

      {!status?.connected ? (
        <div>
          <p style={{ marginBottom: '1rem' }}>
            Configure your PayPal REST API credentials to start accepting subscription payments.
          </p>
          <div style={{ background: '#f3f4f6', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
            <strong>How to get your credentials:</strong>
            <ol style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>Go to <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noopener noreferrer" style={{color: '#667eea'}}>PayPal Developer Dashboard</a></li>
              <li>Create a new app or select an existing one</li>
              <li>Copy your Client ID and Secret</li>
              <li>Choose Sandbox for testing or Live for production</li>
            </ol>
          </div>

          {!showCredentials ? (
            <button
              className="btn btn-primary"
              onClick={() => setShowCredentials(true)}
            >
              🔧 Configure PayPal Credentials
            </button>
          ) : (
            <div>
              <div className="form-group">
                <label>Environment *</label>
                <select
                  className="form-input"
                  value={credentials.environment}
                  onChange={(e) => setCredentials({ ...credentials, environment: e.target.value })}
                >
                  <option value="live">Live (Production)</option>
                  <option value="sandbox">Sandbox (Testing)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Client ID *</label>
                <input
                  type="text"
                  className="form-input"
                  value={credentials.clientId}
                  onChange={(e) => setCredentials({ ...credentials, clientId: e.target.value })}
                  placeholder="Enter your PayPal Client ID"
                />
              </div>

              <div className="form-group">
                <label>Client Secret *</label>
                <input
                  type="password"
                  className="form-input"
                  value={credentials.clientSecret}
                  onChange={(e) => setCredentials({ ...credentials, clientSecret: e.target.value })}
                  placeholder="Enter your PayPal Client Secret"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveCredentials}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCredentials(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ background: '#10b981', color: 'white', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
            <strong>✓ PayPal Configured</strong>
            <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
              <div>Client ID: {status.client_id?.substring(0, 20)}...</div>
              <div>Environment: <strong>{status.environment}</strong></div>
              <div>Configured: {new Date(status.created_at).toLocaleDateString()}</div>
            </div>
          </div>

          {!showCredentials ? (
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button className="btn btn-secondary" onClick={handleTest}>
                Test Connection
              </button>
              <button className="btn btn-secondary" onClick={() => {
                setCredentials({
                  clientId: status.client_id || '',
                  clientSecret: '',
                  environment: status.environment || 'sandbox'
                });
                setShowCredentials(true);
              }}>
                Update Credentials
              </button>
              <button className="btn btn-danger" onClick={handleDisconnect}>
                Remove Configuration
              </button>
            </div>
          ) : (
            <div>
              <div className="form-group">
                <label>Environment *</label>
                <select
                  className="form-input"
                  value={credentials.environment}
                  onChange={(e) => setCredentials({ ...credentials, environment: e.target.value })}
                >
                  <option value="live">Live (Production)</option>
                  <option value="sandbox">Sandbox (Testing)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Client ID *</label>
                <input
                  type="text"
                  className="form-input"
                  value={credentials.clientId}
                  onChange={(e) => setCredentials({ ...credentials, clientId: e.target.value })}
                  placeholder="Enter your PayPal Client ID"
                />
              </div>

              <div className="form-group">
                <label>Client Secret * (leave blank to keep existing)</label>
                <input
                  type="password"
                  className="form-input"
                  value={credentials.clientSecret}
                  onChange={(e) => setCredentials({ ...credentials, clientSecret: e.target.value })}
                  placeholder="Enter new Client Secret"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveCredentials}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCredentials(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
            <strong>Webhook URL:</strong>
            <p style={{ margin: '0.5rem 0', fontFamily: 'monospace', background: 'white', padding: '0.5rem', borderRadius: '4px' }}>
              https://mumble.steinmetz.ltd/api/webhooks/paypal
            </p>
            <small style={{ color: '#666' }}>
              Add this URL to your PayPal webhook settings to receive subscription updates.
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayPalSettings;
